
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import type { SubscriptionTier } from "@/utils/subscriptionUtils";
import { getSubscriptionDisplayName } from "@/utils/subscriptionUtils";

interface UpgradeResult {
  success: boolean;
  message: string;
}


export const useSubscriptionUpgrade = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isUpgrading, setIsUpgrading] = useState(false);

  const upgradeSubscription = async (targetTier: SubscriptionTier): Promise<UpgradeResult> => {
    if (!user) {
      return { success: false, message: "User not authenticated" };
    }

    setIsUpgrading(true);

    try {
      // Update user's subscription tier in the database (no conversion needed)
      const { error } = await supabase
        .from('user_profiles')
        .update({ subscription_tier: targetTier })
        .eq('user_id', user.id);

      if (error) throw error;

      // Create subscription record
      const planPrices: Record<SubscriptionTier, number> = {
        alpha200: 0,
        alpha600: 14.99,
        alpha_unlimited: 29.99
      };

      if (targetTier !== 'alpha200') {
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: user.id,
            plan_name: targetTier, // Direct tier name usage
            amount: planPrices[targetTier],
            currency: 'USD',
            status: 'active',
            start_date: new Date().toISOString(),
            end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
          });

        if (subscriptionError) throw subscriptionError;
      }

      // Show success toast
      toast({
        title: "Upgrade Successful! 🚀",
        description: `Welcome to ${getSubscriptionDisplayName(targetTier)}! Your enhanced limits are now active.`,
      });

      return { success: true, message: "Upgrade successful" };
    } catch (error) {
      console.error('Subscription upgrade error:', error);
      
      // Show error toast
      toast({
        title: "Upgrade Failed",
        description: "There was an issue upgrading your subscription. Please try again.",
        variant: "destructive",
      });

      return { success: false, message: "Upgrade failed" };
    } finally {
      setIsUpgrading(false);
    }
  };

  return {
    upgradeSubscription,
    isUpgrading
  };
};
